import {
  HubConnection,
  HubConnectionBuilder,
} from "@microsoft/signalr";
import { Environment } from "../environment/app-settings";
import { HubMethods } from '../models/hub.methods.model'
import { SessionStorage } from "./cookie-storage";
import { STORAGEKEYS } from "./keys";


export enum HUB_CONNECTION_STATUS {
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  RECONNECTING = 'RECONNECTING',
}

export interface IHubConnectionStatus {
  status: HUB_CONNECTION_STATUS;
  isConnected: boolean | undefined;
  isFinished: boolean | undefined;
  isValid: boolean | undefined;
  userSessionSessionStorageValue: string | null;
  error: string | undefined;
}


export class HubClient {
  private readonly _connection: HubConnection;
  private readonly _apiUrl: string;
  private readonly _sessionStorage : SessionStorage;

  private _status: HUB_CONNECTION_STATUS = HUB_CONNECTION_STATUS.DISCONNECTED;
  private _onStatusChange?: (status: HUB_CONNECTION_STATUS) => void;

  public static readonly hubMethods: HubMethods = {
    bmVerifyUrl: 'bm_verify_url',
    applicationRejected: 'rejected',
    documentsRedy: 'documents_ready', 
    sessionTimeout: 'session_timeout',
    calculateOffer: 'calculate_offer',
    mobManualVerification: 'mobywatel_identification',
    clientChoiceIdentification: 'client_choice_identification',
    contractDone: 'contract_done',
    validationManualWaitForContact: 'validation_manual_wait_for_contact',
    validationFailedWaitForContact: 'validation_failed_wait_for_contact',
    waitForValidationCompletion: 'wait_for_validation_completion',
    mobProcessing : 'mob_processing',
    preparedForDisbursed: 'prepared_for_disbursed',
  };

  constructor(hubName: string) {
    this._apiUrl = `${Environment.apiUrl}${hubName}`;
    this._sessionStorage = new SessionStorage();
    this._connection = this.createConnection(this._apiUrl);

    this.registerConnectionEvents();
  }

    // PUBLIC API
    public onStatusChange(callback: (status: HUB_CONNECTION_STATUS) => void) {
      this._onStatusChange = callback;
    }

    public get status() {
      return this._status;
    }

          
    public subscribe<T>(methodName: string, callback: (data: T) => void) {
      this._connection.on(methodName, callback);
    }

    public async start() {
      try {
        console.log(">>> Hub client starting...");
        await this._connection.start();
        this.setStatus(HUB_CONNECTION_STATUS.CONNECTED);
      } 
      catch (err) {
        console.log(">>> Hub start error, retry in 5s");
        this.setStatus(HUB_CONNECTION_STATUS.DISCONNECTED);

        setTimeout(() => this.start(), 5000)
      }
    }

    public async stop() {
      await this._connection.stop();
      this.setStatus(HUB_CONNECTION_STATUS.DISCONNECTED);
    }

    // PRIVATE API
    private setStatus(status: HUB_CONNECTION_STATUS) {
      this._status = status;
      this._onStatusChange?.(status);
    }

    private registerConnectionEvents() {
      this._connection.onreconnecting(() => {
        console.log(">>> HUB reconnecting...");
        this.setStatus(HUB_CONNECTION_STATUS.RECONNECTING);
      });

      this._connection.onreconnected(() => {
        console.log(">>> HUB reconnected");
        this.setStatus(HUB_CONNECTION_STATUS.CONNECTED);
      });

      this._connection.onclose(() => {
        console.log(">>> HUB disconnected");
        this.setStatus(HUB_CONNECTION_STATUS.DISCONNECTED);
      });
    }
      
    private configureConnection(apiUrl: string): HubConnectionBuilder {
      return new HubConnectionBuilder()
      .withUrl(apiUrl, {
        accessTokenFactory: () =>
          this._sessionStorage.get(STORAGEKEYS.ACCESS_TOKEN),
      })
      .withAutomaticReconnect();
    }

    
    private createConnection(apiUrl: string): HubConnection {
      return this.configureConnection(apiUrl).build();
    }
}
